import ClassNames from "classnames";
import _ from "lodash";
import { useEffect, useState } from "react";
import { dayJS, utcToLocal } from "../../utils/datetime";
import styles from "./index.module.scss";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import AssignmentIcon from "@material-ui/icons/Assignment";
import { useTranslation } from "react-i18next";
import { SubjectableType } from "../../enums/SubjectableType";
import { decimalCheck } from "../../utils/customValidation";

// example checkpoints data:
//
// checkpoints: [
//   {
//     checkpoint_date: "2021-03-29 12:00:00",
//     subject_id: 68,
//     subject: "Quality ",
//     checkpoint_progress_value: null,
//     action_at_checkpoint: null,
//   },
//   {
//     checkpoint_date: "2021-03-30 12:00:00",
//     subject_id: 68,
//     subject: "Quality ",
//     checkpoint_progress_value: null,
//     action_at_checkpoint: null,
//   },
//   {
//     checkpoint_date: "2021-03-29 12:00:00",
//     subject_id: 69,
//     subject: "Productivity ",
//     checkpoint_progress_value: null,
//     action_at_checkpoint: null,
//   },
//   {
//     checkpoint_date: "2021-03-30 12:00:00",
//     subject_id: 69,
//     subject: "Productivity ",
//     checkpoint_progress_value: null,
//     action_at_checkpoint: null,
//   },
// ],

// for validation checking purposes
let fields = [
  // { identifier: "(69)2021-03-30 12:00:00", valid: true }
];

// check fields validity
const checkFieldsValidity = () => {
  let valid = true;
  fields.forEach(o => {
    if (o.valid === false) {
      valid = false;
    }
  });
  return valid;
};

const updateFieldsArray = (subjectId, date, fieldValue) => {
  let fIndex = fields.findIndex(o => {
    return o.identifier === `(${subjectId})${date}`;
  });

  if (fIndex < 0) {
    // field doesnt exist in fields array. make one
    fields.push({
      identifier: `(${subjectId})${date}`,
      valid: !!fieldValue && !isNaN(fieldValue),
    });
  } else {
    // field exists in fields array. update valid value
    fields[fIndex].valid = !!fieldValue && !isNaN(fieldValue);
  }
};

function FinalScore({
  reviewYetToHappen,
  subject,
  finalScores,
  setFinalScores,
  setDialogOpen,
  setActiveCheckpoint,
  setIsFilledAndValid,
  disabled,
}) {
  const [reviewPreviouslyScored, setReviewPreviouslyScored] = useState(false);
  const [isFinalDateDisable, setIsFinalDateDisable] =
    useState(reviewYetToHappen);

  useEffect(() => {
    setIsFinalDateDisable(reviewYetToHappen);
  }, [reviewYetToHappen]);
  // update ReviewPreviouslyScored
  useEffect(() => {
    let finalObj = finalScores.find(o => {
      return o.subject_id === subject.subject_id;
    });

    setReviewPreviouslyScored(
      // is previously scored if:
      !!finalObj.review_progress_score && // has a value, AND
      !finalObj.isDirty // is not dirty, AND
    );
  }, [finalScores, subject.subject_id]);

  const handleFinalValueChange = (newVal, id) => {
    newVal = decimalCheck(newVal, 2);
    let newFinalScores = _.cloneDeep(finalScores);
    let index = newFinalScores.findIndex(o => {
      return o.subject_id === id;
    });
    newFinalScores[index]["review_progress_score"] = decimalCheck(newVal, 2);
    console.log("newFinalScores", newFinalScores);
    newFinalScores[index]["isDirty"] = true;
    setFinalScores(newFinalScores);
    if (localStorage.sessionCheckPointData) {
      const tmp = JSON.parse(localStorage.sessionCheckPointData);
      tmp.finalScores = newFinalScores;
      localStorage.setItem("sessionCheckPointData", JSON.stringify(tmp));
    }
  };

  useEffect(() => {
    let finalObj = finalScores.find(o => {
      return o.subject_id === subject.subject_id;
    });
    // update fields array for this field. for validation purposes
    let isFillable = !(isFinalDateDisable || reviewPreviouslyScored);
    if (isFillable) {
      updateFieldsArray(
        finalObj.subject_id,
        finalObj.review_datetime,
        finalObj.review_progress_score
      );
    }
  }, [
    isFinalDateDisable,
    reviewPreviouslyScored,
    finalScores,
    subject.subject_id,
  ]);

  return (
    <div
      className={ClassNames(
        styles.subjectScore,
        disabled || isFinalDateDisable ? styles.disabled : ""
      )}
    >
      {isFinalDateDisable ? (
        <div className={styles.subjectScorePlaceholder}></div>
      ) : (
        <div className={styles.subjectScoreWrapper}>
          <TextField
            variant="outlined"
            className={styles.subjectScore__input}
            disabled={disabled || isFinalDateDisable || reviewPreviouslyScored}
            value={
              finalScores.find(o => {
                return o.subject_id === subject.subject_id;
              }).review_progress_score
                ? finalScores.find(o => {
                  return o.subject_id === subject.subject_id;
                }).review_progress_score
                : ""
            }
            onChange={e => {
              handleFinalValueChange(e.target.value, subject.subject_id);
              // update fields array
              updateFieldsArray(
                subject.subject_id,
                subject.checkpoint_date,
                e.target.value
              );

              // check fields validity
              setIsFilledAndValid(checkFieldsValidity());
            }}
          />
          <div
            className={ClassNames(
              styles.subjectScore__btn,
              disabled || reviewPreviouslyScored ? styles.disabled : ""
            )}
          >
            <Button
              variant="contained"
              disableRipple
              disableElevation
              disabled={disabled || reviewPreviouslyScored}
              onClick={() => {
                if (setDialogOpen) {
                  setDialogOpen(true);
                  let index = finalScores.findIndex(o => {
                    return o.subject_id === subject.subject_id;
                  });
                  setActiveCheckpoint(finalScores[index]);
                }
              }}
            >
              <AssignmentIcon />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CheckpointScoreSubject({
  checkpoints,
  setCheckpoints,
  finalScores,
  setFinalScores,
  reviewDate,
  dateOverride,
  dialogOpen,
  setDialogOpen,
  setActiveCheckpoint,
  setIsFilledAndValid,
  disabled,
  ...props
}) {
  const { t } = useTranslation();
  const [subjects, setSubjects] = useState([]);
  const [reviewYetToHappen, setReviewYetToHappen] = useState(true);

  useEffect(() => {
    setReviewYetToHappen(false);
  }, []);

  // checks if current date already reached or elapsed review date.
  // if so, reviewYetToHappen = false
  useEffect(() => {
    let currDate = dayJS(new Date());
    let revDate = utcToLocal(reviewDate).startOf("D");
    setReviewYetToHappen(currDate.isBefore(revDate));
  }, [reviewDate]);

  const handleCheckpointValueChange = (newVal, index) => {
    newVal = decimalCheck(newVal, 2);
    let newCheckpoints = _.cloneDeep(checkpoints);
    newCheckpoints[index]["checkpoint_progress_value"] = decimalCheck(
      newVal,
      2
    );
    console.log("newCheckpoints", newCheckpoints);
    newCheckpoints[index]["isDirty"] = true;
    setCheckpoints(newCheckpoints);
    if (localStorage.sessionCheckPointData) {
      const tmp = JSON.parse(localStorage.sessionCheckPointData);
      tmp.checkpoints = newCheckpoints;
      localStorage.setItem("sessionCheckPointData", JSON.stringify(tmp));
    }
  };

  useEffect(() => {
    // compute subjects
    let newSubjects = [];
    let uniqueIDs = [];
    checkpoints.forEach(cp => {
      if (!uniqueIDs.includes(cp.subject_id)) {
        uniqueIDs.push(cp.subject_id);
        newSubjects.push(cp);
      }
    });
    setSubjects(newSubjects);
  }, [checkpoints]);

  const getFinalObj = subjectId => {
    return finalScores.find(o => {
      return o.subject_id === subjectId;
    });
  };

  return (
    <>
      {subjects.map((subject, index) => {
        return (
          <div className={styles.checkpointScoreSubject} key={index}>
            <div className={styles.subjectHeader}>
              <div className={styles.subjectHeaderTitle}>
                {subject?.subjectable_type === SubjectableType.FLASH_REWARD_USER_KPIS ? '(Flash Reward) ' : ''}
                {subject?.sub_subject
                  ? `${subject.subject}  —  ${subject.sub_subject}`
                  : `${subject.subject}`}
              </div>

              {getFinalObj(subject.subject_id) && (
                <div className={styles.subjectHeaderStats}>
                  <div className={styles.subjectHeaderStat}>
                    <span className={styles.label}>
                      {t("updateCheckpoint.previous")}:
                    </span>
                    <span className={styles.value}>
                      {getFinalObj(subject.subject_id).previous_score}
                    </span>
                  </div>
                  <div className={styles.subjectHeaderStat}>
                    <span className={styles.label}>
                      {" "}
                      {t("growForm.target")}:
                    </span>
                    <span className={styles.value}>
                      {getFinalObj(subject.subject_id).target_score}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.subjectScores}>
              {checkpoints
                .filter(cp => {
                  return cp.subject_id === subject.subject_id;
                })
                .sort((a, b) => {
                  return (
                    new Date(a.checkpoint_date) - new Date(b.checkpoint_date)
                  );
                })
                .map((item, index) => {
                  let cpDate = new Date(item.checkpoint_date);
                  cpDate.setHours(0); // set date boundary to 00:00 hrs
                  let currDate = new Date();
                  let yetToHappen = currDate < cpDate;
                  let previouslyScored =
                    !!item.checkpoint_progress_value && !item.isDirty;

                  // developer debug tool: override having to wait till the date
                  if (dateOverride) {
                    yetToHappen = false;
                  }

                  // update fields array for this field. for validation purposes
                  let isFillable = !(yetToHappen || previouslyScored);
                  if (isFillable) {
                    updateFieldsArray(
                      item.subject_id,
                      item.checkpoint_date,
                      item.checkpoint_progress_value
                    );
                  }

                  return (
                    <div
                      className={ClassNames(
                        styles.subjectScore,
                        disabled || yetToHappen ? styles.disabled : ""
                      )}
                      key={index}
                    >
                      {yetToHappen ? (
                        <div className={styles.subjectScorePlaceholder}></div>
                      ) : (
                        <div className={styles.subjectScoreWrapper}>
                          <TextField
                            variant="outlined"
                            className={styles.subjectScore__input}
                            disabled={disabled || yetToHappen || previouslyScored}
                            value={
                              item.checkpoint_progress_value
                                ? item.checkpoint_progress_value
                                : ""
                            }
                            onChange={e => {
                              let index = _.findIndex(checkpoints, o => {
                                return (
                                  o.checkpoint_date === item.checkpoint_date &&
                                  o.subject_id === item.subject_id
                                );
                              });
                              handleCheckpointValueChange(
                                e.target.value,
                                index
                              );

                              // update fields array
                              updateFieldsArray(
                                item.subject_id,
                                item.checkpoint_date,
                                e.target.value
                              );

                              // check fields validity

                              setIsFilledAndValid(checkFieldsValidity());
                            }}
                          />
                          <div
                            className={ClassNames(
                              styles.subjectScore__btn,
                              disabled || previouslyScored ? styles.disabled : ""
                            )}
                          >
                            {/* TODO: dialog box for validation method selection */}
                            <Button
                              variant="contained"
                              disableRipple
                              disableElevation
                              disabled={disabled || previouslyScored}
                              onClick={() => {
                                if (setDialogOpen) {
                                  setDialogOpen(true);
                                  let index = _.findIndex(checkpoints, o => {
                                    return (
                                      o.checkpoint_date ===
                                      item.checkpoint_date &&
                                      o.subject_id === item.subject_id
                                    );
                                  });
                                  setActiveCheckpoint(checkpoints[index]);
                                }
                              }}
                            >
                              <AssignmentIcon />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

              <FinalScore
                reviewYetToHappen={dateOverride ? false : reviewYetToHappen}
                subject={subject}
                finalScores={finalScores}
                setFinalScores={setFinalScores}
                setDialogOpen={setDialogOpen}
                setActiveCheckpoint={setActiveCheckpoint}
                setIsFilledAndValid={setIsFilledAndValid}
                disabled={disabled}
              />
            </div>
          </div>
        );
      })}
    </>
  );
}